/**
 * Vue Router configuration with navigation guards.
 *
 * Routes:
 *   /login                — user login page
 *   /dashboard            — user profile (default dashboard landing)
 *   /market               — browse projects (legacy alias)
 *   /projects             — browse projects
 *   /projects/:projectPda — project detail
 *   /market/:projectPda   — project detail (legacy alias)
 *   /portfolio            — user investments
 *   /settings             — user settings
 *   /admin/login          — admin login
 *   /admin/dashboard      — admin dashboard
 *   /participant/*        — participant role routes
 *   /partner/*            — partner role routes
 *   /user/*               — user (hotel guest) role routes
 *   /forbidden            — 403 forbidden page
 *   /:pathMatch(.*)*      — 404 catch-all
 *
 * Guards:
 *   - Unauthenticated users → /login
 *   - Non-admin users → blocked from /admin/*
 *   - Users without on-chain UserWallet PDA → onboarding
 *   - Cedar-style RBAC: Navigate action checked against policies.json
 */

import { useAuthorizationStore } from '@/stores/authorization'
import { useOnboardingStore } from '@/stores/onboarding'
import { useWalletStore } from '@/stores/wallet'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// ---------------------------------------------------------------------------
// Route definitions — lazy-loaded for code splitting
// ---------------------------------------------------------------------------

const routes: RouteRecordRaw[] = [
    // Public routes (no auth required)
    {
        path: '/',
        name: 'login',
        component: () => import('@/pages/LoginPage.vue'),
        meta: { requiresAuth: false },
    },

    // Authenticated dashboard routes (wrapped in DashboardLayout)
    {
        path: '',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                redirect: '/dashboard',
            },
            {
                path: '/dashboard',
                name: 'dashboard',
                component: () => import('@/pages/DashboardPage.vue'),
            },
            {
                path: '/market',
                name: 'market',
                component: () => import('@/pages/CampaignsPage.vue'),
            },
            {
                path: '/projects',
                name: 'projects',
                component: () => import('@/pages/CampaignsPage.vue'),
            },
            {
                path: '/market/:projectPda',
                name: 'project-detail',
                component: () => import('@/pages/ProjectDetailPage.vue'),
                props: true,
            },
            {
                path: '/projects/:projectPda',
                name: 'projects-detail',
                component: () => import('@/pages/ProjectDetailPage.vue'),
                props: true,
            },
            {
                path: '/portfolio',
                name: 'portfolio',
                component: () => import('@/pages/PortfolioPage.vue'),
            },
            {
                path: '/settings',
                name: 'settings',
                component: () => import('@/pages/SettingsPage.vue'),
            },
        ],
    },

    // Admin routes (wrapped in DashboardLayout, requires admin role)
    {
        path: '/admin',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true, requiresAdmin: true },
        children: [
            {
                path: 'dashboard',
                name: 'admin-dashboard',
                component: () => import('@/pages/AdminDashboardPage.vue'),
            },
            {
                path: 'projects',
                name: 'admin-projects',
                component: () => import('@/pages/AdminProjectsPage.vue'),
            },
            {
                path: 'projects/:projectPda',
                name: 'admin-project-edit',
                component: () => import('@/pages/AdminProjectEditPage.vue'),
            },
            {
                path: 'withdrawals',
                name: 'admin-withdrawals',
                component: () => import('@/pages/AdminWithdrawalsPage.vue'),
            },
            {
                path: 'distribution',
                name: 'admin-distribution',
                component: () => import('@/pages/AdminDistributionPage.vue'),
            },
            {
                path: 'users',
                name: 'admin-users',
                component: () => import('@/pages/AdminUsersPage.vue'),
            },
            {
                path: 'config',
                name: 'admin-config',
                component: () => import('@/pages/AdminConfigPage.vue'),
            },
            {
                path: 'multisig',
                name: 'admin-multisig',
                component: () => import('@/pages/AdminMultisigPage.vue'),
            },
            {
                path: 'withdraw',
                name: 'admin-withdraw',
                component: () => import('@/pages/WithdrawPage.vue'),
            },
            {
                path: 'profile',
                name: 'admin-profile',
                component: () => import('@/pages/ProfilePage.vue'),
            },
        ],
    },

    // Participant role routes — use DashboardLayout
    {
        path: '/participant',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                redirect: '/participant/dashboard',
            },
            {
                path: 'dashboard',
                name: 'participant-dashboard',
                component: () => import('@/pages/ParticipantDashboardPage.vue'),
            },
            {
                path: 'deposit',
                name: 'participant-deposit',
                component: () => import('@/pages/DepositPage.vue'),
            },
            {
                path: 'withdraw',
                name: 'participant-withdraw',
                component: () => import('@/pages/WithdrawPage.vue'),
            },
            {
                path: 'projects',
                name: 'participant-projects',
                component: () => import('@/pages/CampaignsPage.vue'),
            },
            {
                path: 'projects/:projectPda',
                name: 'participant-project-detail',
                component: () => import('@/pages/ProjectDetailPage.vue'),
                props: true,
            },
            {
                path: 'portfolio',
                name: 'participant-portfolio',
                component: () => import('@/pages/PortfolioPage.vue'),
            },
            {
                path: 'profile',
                name: 'participant-profile',
                component: () => import('@/pages/ProfilePage.vue'),
            },
        ],
    },

    // Partner role routes — use DashboardLayout
    {
        path: '/partner',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: 'dashboard',
                name: 'partner-dashboard',
                component: () => import('@/pages/PartnerDashboardPage.vue'),
            },
            {
                path: 'bookings',
                name: 'partner-bookings',
                component: () => import('@/pages/PartnerBookingsPage.vue'),
            },
            {
                path: 'withdraw',
                name: 'partner-withdraw',
                component: () => import('@/pages/WithdrawPage.vue'),
            },
            {
                path: 'profile',
                name: 'partner-profile',
                component: () => import('@/pages/ProfilePage.vue'),
            },
        ],
    },

    // User (hotel guest) role routes (future — use DashboardLayout)
    {
        path: '/user',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: 'dashboard',
                name: 'user-dashboard',
                component: () => import('@/pages/UserDashboardPage.vue'),
            },
            {
                path: 'hotels',
                name: 'user-hotels',
                component: () => import('@/pages/CampaignsPage.vue'),
            },
            {
                path: 'hotels/:projectPda',
                name: 'user-hotel-detail',
                component: () => import('@/pages/ProjectDetailPage.vue'),
                props: true,
            },
            {
                path: 'bookings',
                name: 'user-bookings',
                component: () => import('@/pages/PortfolioPage.vue'),
            },
            {
                path: 'payments',
                name: 'user-payments',
                component: () => import('@/pages/PaymentsPage.vue'),
            },
            {
                path: 'withdraw',
                name: 'user-withdraw',
                component: () => import('@/pages/WithdrawPage.vue'),
            },
            {
                path: 'profile',
                name: 'user-profile',
                component: () => import('@/pages/ProfilePage.vue'),
            },
        ],
    },

    // Auth callback routes (public, no auth guard)
    {
        path: '/auth/verify',
        name: 'magic-link-verify',
        component: () => import('@/pages/MagicLinkVerifyPage.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/auth/callback',
        name: 'oauth-callback',
        component: () => import('@/pages/OAuthCallbackPage.vue'),
        meta: { requiresAuth: false },
    },

    // Public legal / help pages (no auth required)
    {
        path: '/privacy',
        name: 'privacy',
        component: () => import('@/pages/PrivacyPage.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/terms',
        name: 'terms',
        component: () => import('@/pages/TermsPage.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/help',
        name: 'help',
        component: () => import('@/pages/HelpCenterPage.vue'),
        meta: { requiresAuth: false },
    },

    // 403 Forbidden
    {
        path: '/forbidden',
        name: 'forbidden',
        component: () => import('@/pages/ForbiddenPage.vue'),
        meta: { requiresAuth: false },
    },

    // 404 catch-all
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFoundPage.vue'),
        meta: { requiresAuth: false },
    },
]

// ---------------------------------------------------------------------------
// Router instance
// ---------------------------------------------------------------------------

export const router = createRouter({
    history: createWebHistory(),
    routes,
})

// ---------------------------------------------------------------------------
// Navigation guards
// ---------------------------------------------------------------------------

/** Routes that bypass both authentication and authorization checks */
const PUBLIC_ROUTES = new Set(['login', 'not-found', 'forbidden', 'magic-link-verify', 'oauth-callback'])

/**
 * Role-specific routes that require Cedar-style RBAC evaluation.
 * Routes starting with these prefixes are checked against policies.json.
 */
const ROLE_ROUTE_PREFIXES = ['/admin/', '/participant/', '/partner/', '/user/']

/** Role-to-dashboard path mapping for authenticated redirects. */
const ROLE_DASHBOARDS: Record<string, string> = {
    Admin: '/admin/dashboard',
    Participant: '/participant/dashboard',
    Partner: '/partner/dashboard',
    User: '/user/dashboard',
}

const SESSION_EXPIRED_EVENT = 'nomadian:session-expired'

function dispatchSessionExpired() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
}



router.beforeEach(async (to, _from) => {
    const walletStore = useWalletStore()
    const onboardingStore = useOnboardingStore()
    const authzStore = useAuthorizationStore()

    // Resolve auth requirement: check route meta or parent meta
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)
    const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin)
    const isPublic = PUBLIC_ROUTES.has(to.name as string) || !requiresAuth

    // 1. Check Supabase session directly (async). Local wallet/role cache is
    //    not sufficient for auth because it can outlive the Supabase session.
    let hasSupabaseSession = false
    try {
        const { supabase } = await import('@/lib/supabase')
        const { data } = await supabase.auth.getSession()
        hasSupabaseSession = !!data.session
    } catch {
        hasSupabaseSession = false
    }

    // Always refresh role from JWT to override stale cached values
    if (hasSupabaseSession) {
        const jwtRole = authzStore.syncRoleFromJwt()
        if (jwtRole && jwtRole !== authzStore.role) {
            authzStore.syncRole(jwtRole)
        }
    }

    const hasStaleLocalAuth = walletStore.isConnected || authzStore.isRoleAssigned
    const isAuthenticated = hasSupabaseSession

    // 2. If user is authenticated and navigating to login/root, redirect to role dashboard.
    //    Skip this redirect when arriving from an explicit sign-out (query ?signout=1)
    //    because the Supabase client may still cache an in-memory session briefly.
    if (isAuthenticated && to.name === 'login' && !to.query.signout) {
        const role = authzStore.role
        if (role && ROLE_DASHBOARDS[role]) {
            return ROLE_DASHBOARDS[role]
        }
        return { name: 'dashboard' }
    }

    // 3. If route requires auth but user is not authenticated, redirect to login.
    //    When stale local auth exists, clear it and show the session-expired modal.
    if (!isPublic && !isAuthenticated) {
        if (hasStaleLocalAuth) {
            await walletStore.disconnect()
            authzStore.clearRole()
            dispatchSessionExpired()
            return { name: 'login', query: { expired: '1' } }
        }

        return { name: 'login', query: { redirect: to.fullPath } }
    }

    // 3. If route requires admin but user role is not Admin
    if (requiresAdmin && authzStore.role !== 'Admin') {
        return { name: 'forbidden' }
    }

    // 4. Session timeout: if wallet has JWT but session expired, force re-login
    if (walletStore.isConnected && walletStore.jwt && walletStore.isSessionExpired && requiresAuth) {
        walletStore.disconnect()
        authzStore.clearRole()
        return { name: 'login', query: { expired: '1' } }
    }

    // 5. If user is connected but onboarding is not complete, AND they don't
    //    have an assigned role yet, redirect to generic dashboard for onboarding.
    //    Users with roles go to their role-specific dashboards which handle own state.
    if (
        walletStore.isConnected &&
        !onboardingStore.isOnboardingComplete &&
        !authzStore.isRoleAssigned &&
        requiresAuth &&
        to.name !== 'dashboard' &&
        to.name !== 'settings'
    ) {
        return { name: 'dashboard' }
    }

    // 6. Cedar-style RBAC: check Navigate action for role-specific routes
    //    Only enforced when the user has an assigned role and the route is
    //    within a role-specific prefix.
    if (
        authzStore.isRoleAssigned &&
        ROLE_ROUTE_PREFIXES.some((prefix) => to.path.startsWith(prefix))
    ) {
        if (!authzStore.checkRouteAccess(to.path)) {
            return { name: 'forbidden' }
        }
    }

    // 7. If navigating to generic /dashboard, user has a role, AND onboarding
    //    is complete, redirect to their role-specific dashboard.
    //    (If onboarding is incomplete, keep them on generic /dashboard
    //    so the OnboardingOverlay can enforce registration.)
    if (to.name === 'dashboard' && authzStore.role && onboardingStore.isOnboardingComplete) {
        const roleDashboard = ROLE_DASHBOARDS[authzStore.role]
        if (roleDashboard) {
            return roleDashboard
        }
    }

    // Allow navigation
    return true
})

// ---------------------------------------------------------------------------
// Type augmentation for route meta
// ---------------------------------------------------------------------------

declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean
        requiresAdmin?: boolean
    }
}
